import { DID_STORAGE_NAME } from '~/core/constants'
import amplitudeJS from 'amplitude-js'
import createGuest from 'cross-domain-storage/guest'
import envData from '~/core/env'
import { generateGUID } from '~/core/utils'

const amplitude = amplitudeJS.getInstance()
const queryParams = new URLSearchParams(window.location.search)
const abexpid = queryParams.get('abexpid')
const preselectPlan = queryParams.get('preselectPlan')

const EVENT_NAMES = {
  SELECT_PRO_PLAN: 'Select Pro payment plan',
  UPDATE_CARD_INFO: 'Update card info on account suspended',
  UPGRADE_TO_PRO: 'Upgrade to Pro'
}

const EVENT_STATES = {
  INIT: '[INIT]',
  FAIL: '[FAIL]'
}

const PAGE_NAMES = {
  PAYMENTS_PAGE: 'View Payments page',
  PAYMENTS_SUCCESS_PAGE: 'View Payments Success page',
  GUEST_PLANS: 'View guest Plan selection page'
}

const USER_TYPE = {
  VPAAS: 'vpaas',
  STANDALONE: 'standalone'
}

let sharedStorage

let initInProgress

const trackBatch = []

const getSharedStorage = async () => {
  if (sharedStorage) {
    return sharedStorage
  }

  const url = `${envData.webUrl}/static/accessStorage.html`

  try {
    const isStorageAvailable = await fetch(url, {
      method: 'HEAD'
    })

    if (isStorageAvailable.ok) {
      sharedStorage = createGuest(url)
      return sharedStorage
    }
  } catch (err) {
    throw new Error(err)
  }

  throw new Error('Failed to access shared storage')
}

const getDeviceID = async () => {
  let id
  try {
    const storage = await getSharedStorage()
    id = await new Promise(resolve => {
      storage.get(DID_STORAGE_NAME, (error, data) => {
        if (data) {
          resolve(data)
          return
        }
        if (error) {
          console.error({ __filename, error }, 'Failed getting deviceID from shared storage.')
        }
        resolve(null)
      })
    })

    storage.close()
  } catch (e) {
    console.error({ __filename, e }, 'Failed getting deviceID; shared storage unavailable')
  }
  return id
}

const setDeviceID = id => async () => {
  try {
    const storage = await getSharedStorage()
    storage.set(DID_STORAGE_NAME, id, error => {
      if (error) {
        console.error({ __filename, error }, 'Failed setting deviceID to shared storage.')
      }

      storage.close()
    })
  } catch (e) {
    console.error({ __filename, e }, 'Failed setting deviceID; shared storage unavailable')
  }
}

const updateUserIdentity = userIdentity => {
  if (initInProgress) {
    trackBatch.unshift(() => updateUserIdentity(userIdentity))
    return
  }

  const source = 'VideoMeetingsWeb'
  let traits

  if (userIdentity) {
    const { customerId, ext, email, hasSubscription, userType } = userIdentity
    traits = {
      authenticated: true,
      googleAnalyticsId: window.googleAnalyticsId,
      source,
      customerId,
      ext,
      email,
      pbx: userIdentity.pbx || userIdentity.tenant,
      env: envData.env
    }

    if (hasSubscription !== undefined) {
      traits.hasSubscription = hasSubscription
    }

    if (userType !== undefined) {
      traits.userType = userType
    }
  } else {
    traits = {
      hasSubscription: false,
      authenticated: false,
      googleAnalyticsId: window.googleAnalyticsId,
      env: envData.env,
      source
    }
  }

  const identity = new amplitude.Identify()
  Object.keys(traits).forEach(key => identity.set(key, traits[key]))

  amplitude.setUserId(userIdentity ? userIdentity.userId : null)
  amplitude.identify(identity)
}

export const updateDeviceID = async id => {
  await setDeviceID(id)
  amplitude.setDeviceId(id)
}

export const assignIDs = async () => {
  let id = await getDeviceID()
  if (!id) {
    id = generateGUID()
    await setDeviceID(id)
  }

  amplitude.setDeviceId(id)

  return id
}

export const initAnalytics = async userIdentity => {
  initInProgress = true
  const userId = userIdentity ? userIdentity.userId : null

  amplitude.init(envData.analyticsKey, userId, {
    includeReferrer: true,
    saveParamsReferrerOncePerSession: false
  }, async () => {
    await assignIDs()
    initInProgress = false

    updateUserIdentity(userIdentity)
    while (trackBatch.length) {
      trackBatch.pop()()
    }
  })
}

const trackEvent = (name, state, properties = {}) => {
  if (initInProgress) {
    trackBatch.unshift(() => trackEvent(name, state, properties))
    return
  }

  const eventName = state ? `${state} ${name}` : name
  amplitude.logEvent(eventName, {
    ...properties,
    abexpid,
    preselectPlan
  })
}

const trackInitEvent = (name, properties) => {
  trackEvent(name, EVENT_STATES.INIT, properties)
}

const trackFailedEvent = (name, properties) => {
  trackEvent(name, EVENT_STATES.FAIL, properties)
}

const trackPageView = (pageName, props = {}) => {
  if (initInProgress) {
    trackBatch.unshift(() => trackPageView(pageName, props))
    return
  }
  const { referrer } = document
  const storedReferrer = localStorage.getItem('standaloneReferrer')
  const referrerToUse = storedReferrer || referrer || null
  const mergedProps = {
    ...props,
    referrer: referrerToUse
  }

  if (storedReferrer) {
    localStorage.removeItem('standaloneReferrer')
  }

  trackEvent(pageName, false, mergedProps)
}

export default {
  EVENT_NAMES,
  PAGE_NAMES,
  trackEvent,
  trackFailedEvent,
  trackInitEvent,
  trackPageView,
  USER_TYPE
}
